/**
 * Which branch this visitor belongs to, remembered across a brand's subdomains.
 *
 * Ported from `artifacts/customer-order/src/lib/branch-prefs.ts`, whose own comment explains the one
 * decision that matters: the preference is a **cookie scoped to the brand's common parent domain**, not
 * localStorage, because localStorage is per-origin and cannot be shared between `pizzamajstro.sk` and
 * `nesvady.pizzamajstro.sk`. localStorage is kept as a per-origin mirror for brands whose branches share
 * no parent, where a cross-domain cookie is impossible.
 *
 * **What this file must agree with, byte for byte, is `index.html`.** The inline gate reads the same
 * cookie, splits on the same `:`, strips the same `www.` and lowercases the same way — and it runs before
 * this module exists. Any difference between the two is a redirect loop or a flash of the wrong branch,
 * so the parsing lives in one exported function here and is mirrored, deliberately and with a comment, in
 * the script.
 *
 * Measured against the AGOS backend: `GET /central/branches` is **host-resolved** — `?host=`,
 * `X-Franchise-Host`, then the `Host` header — so on `localhost` it correctly answers no branches at all.
 * Franchise 8 has no domain row, and is correctly never offered.
 */

const COOKIE = 'fk_branch'
const LS_KEY = 'fk_branch_domain'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/** Lowercase, strip a leading `www.` and any port. The same normalisation the inline gate does. */
export function normaliseHost(host: string): string {
  return (host || '')
    .toLowerCase()
    .replace(/:.*$/, '')
    .replace(/^www\./, '')
    .trim()
}

/**
 * The longest shared dot-suffix of a set of hostnames — the brand's parent domain.
 *
 * `['pizzamajstro.sk', 'nesvady.pizzamajstro.sk']` → `pizzamajstro.sk`. Below two labels there is no
 * meaningful parent (two unrelated `.sk` domains share only the TLD, and a cookie on `sk` would be
 * refused by every browser anyway), so it answers `null` and the caller falls back to localStorage.
 */
export function commonParentDomain(domains: readonly string[]): string | null {
  const parts = domains
    .map(normaliseHost)
    .filter((domain) => domain !== '')
    .map((domain) => domain.split('.').reverse())
  if (parts.length === 0) return null

  const common: string[] = []
  for (let index = 0; ; index += 1) {
    const label = parts[0]?.[index]
    if (label === undefined) break
    if (!parts.every((candidate) => candidate[index] === label)) break
    common.push(label)
  }
  return common.length < 2 ? null : common.reverse().join('.')
}

/**
 * The stored preference, read exactly as the inline gate reads it.
 *
 * The `split(':')` is not decoration: legacy has written `host:something` into this cookie at some point,
 * and the gate takes what is before the colon. Reading it differently here would send a visitor to a
 * host the gate had already decided against.
 */
export function branchPrefFrom(cookie: string, stored: string | null): string | null {
  const match = /(?:^|;\s*)fk_branch=([^;]+)/.exec(cookie)
  if (match?.[1] !== undefined) {
    const value = normaliseHost(decodeURIComponent(match[1]).split(':')[0] ?? '')
    if (value !== '') return value
  }
  const mirrored = normaliseHost(stored ?? '')
  return mirrored === '' ? null : mirrored
}

/** The `Set-Cookie` value, built where it can be tested rather than inside a side effect. */
export function branchCookie(
  domain: string,
  parentDomain: string | null,
  secure: boolean,
): string | null {
  const host = normaliseHost(domain)
  if (host === '') return null
  let cookie = `${COOKIE}=${encodeURIComponent(host)}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`
  if (parentDomain !== null) cookie += `; domain=${parentDomain}`
  if (secure) cookie += '; Secure'
  return cookie
}

export function clearingCookie(parentDomain: string | null): string {
  let cookie = `${COOKIE}=; path=/; max-age=0; SameSite=Lax`
  if (parentDomain !== null) cookie += `; domain=${parentDomain}`
  return cookie
}

// ─── the side-effecting half, kept thin ──────────────────────────────────────

export function getBranchPref(): string | null {
  try {
    return branchPrefFrom(
      typeof document === 'undefined' ? '' : document.cookie,
      typeof localStorage === 'undefined' ? null : localStorage.getItem(LS_KEY),
    )
  } catch {
    return null
  }
}

export function setBranchPref(domain: string, parentDomain: string | null): void {
  const cookie = branchCookie(
    domain,
    parentDomain,
    typeof location !== 'undefined' && location.protocol === 'https:',
  )
  if (cookie === null) return
  try {
    document.cookie = cookie
  } catch {
    // A browser refusing the cookie is why the mirror below exists.
  }
  try {
    localStorage.setItem(LS_KEY, normaliseHost(domain))
  } catch {
    /* private mode */
  }
}

export function clearBranchPref(parentDomain: string | null): void {
  try {
    document.cookie = clearingCookie(parentDomain)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(LS_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Undo the pre-React splash.
 *
 * `index.html` adds `fk-deciding` to `<html>`, which hides `#root`, and removes it itself after 2 500 ms
 * as a backstop. This is the *intended* removal: called once the gate has decided or the picker has
 * painted, so the primary branch's page is never flashed at somebody who belongs elsewhere.
 */
export function revealApp(): void {
  try {
    document.documentElement.classList.remove('fk-deciding')
  } catch {
    /* ignore */
  }
}
